// frontend/src/components/FulfillmentReport.js

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Paper,
    Alert,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
} from 'recharts';
import { getFulfillmentCounts } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import CustomTable from './CustomTable'; // Import the CustomTable component

const fulfillmentCategories = ['0 Days', '1 Day', '2 Days', '3 Days', '4+ Days'];
const colors = ['#1268b3', '#20c997', '#dc3545', '#ffc107', '#6f42c1', '#e72d88'];

const FulfillmentReport = ({
    granularity,
    startDate,
    endDate,
    selectedClients = [],
    refreshKey = 0,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { showSnackbar } = useSnackbar();

    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getFulfillmentCounts(
                granularity,
                startDate,
                endDate,
                selectedClients
            );

            // Process data to aggregate fulfillment categories per client and include AvgFulfillmentTime
            const clientMap = {};

            data.forEach(item => {
                const client = item.clientName;
                if (!clientMap[client]) {
                    clientMap[client] = {
                        clientName: client,
                        '0 Days': 0,
                        '1 Day': 0,
                        '2 Days': 0,
                        '3 Days': 0,
                        '4+ Days': 0,
                        AvgFulfillmentTime: item.AvgFulfillmentTime,
                    };
                }
                clientMap[client][item.FulfillmentCategory] = item.OrderCount;
            });

            const processedData = Object.values(clientMap);

            setReportData(processedData);
        } catch (error) {
            setError('Unable to load fulfillment data for the selected filters.');
            showSnackbar('Failed to fetch fulfillment report data.', 'error');
            console.error('Fetch Fulfillment Report Data Error:', error);
        } finally {
            setLoading(false);
        }
    }, [endDate, granularity, selectedClients, showSnackbar, startDate]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData, refreshKey]);

    const chartData = useMemo(() => reportData.map((dataPoint) => ({
        label: dataPoint.clientName,
        '0 Days': dataPoint['0 Days'],
        '1 Day': dataPoint['1 Day'],
        '2 Days': dataPoint['2 Days'],
        '3 Days': dataPoint['3 Days'],
        '4+ Days': dataPoint['4+ Days'],
        AvgFulfillmentTime: dataPoint.AvgFulfillmentTime ? parseFloat(dataPoint.AvgFulfillmentTime.toFixed(2)) : 0.0,
    })), [reportData]);

    // Define table columns
    const columns = [
        {
            id: 'clientName',
            label: 'Client Name',
            minWidth: 150,
            align: 'left',
            sortable: true, // Enable sorting
        },
        ...(!isMobile ? fulfillmentCategories.map(category => ({
            id: category,
            label: category,
            minWidth: 120,
            align: 'left',
            sortable: false, // Typically, categorical data may not require sorting
        })) : []),
        {
            id: 'AvgFulfillmentTime',
            label: 'Avg Fulfillment Time (Days)',
            minWidth: 180,
            align: 'left',
            sortable: true, // Enable sorting
        },
    ];

    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    Fulfillment Time Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Working-day fulfillment distribution and average completion time by client.
                </Typography>
            </Box>
                {loading ? (
                    <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : chartData.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>No fulfillment data</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Adjust the date or client filters and refresh the report.
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                                gap: 2,
                                mb: 2,
                            }}
                        >
                                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
                                        Fulfillment Counts per Client
                                    </Typography>
                                    <Box sx={{ width: '100%', height: { xs: 300, sm: 400 } }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={chartData}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="label" />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Legend />
                                                {fulfillmentCategories.map((category, index) => (
                                                    <Bar
                                                        key={category}
                                                        dataKey={category}
                                                        stackId="a"
                                                        fill={colors[index % colors.length]}
                                                    />
                                                ))}
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
                                        Average Fulfillment Time per Client
                                    </Typography>
                                    <Box sx={{ width: '100%', height: { xs: 300, sm: 400 } }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={chartData}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="label" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="AvgFulfillmentTime"
                                                    stroke="#6f42c1"
                                                    activeDot={{ r: 8 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                        </Box>

                        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, overflow: 'hidden' }}>
                            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 800 }}>
                                Fulfillment Summary Table
                            </Typography>
                            <CustomTable
                                columns={columns}
                                data={reportData.map(row => ({
                                    clientName: row.clientName,
                                    '0 Days': row['0 Days'],
                                    '1 Day': row['1 Day'],
                                    '2 Days': row['2 Days'],
                                    '3 Days': row['3 Days'],
                                    '4+ Days': row['4+ Days'],
                                    AvgFulfillmentTime: row.AvgFulfillmentTime ? row.AvgFulfillmentTime.toFixed(2) : '0.00',
                                }))}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                handleChangePage={handleChangePage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </>
                )}
        </Box>
    );

};

export default FulfillmentReport;
